import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { twMerge } from 'tailwind-merge';
import { formatBookableCheckInDate } from '@utils/booking-lead-time';
export interface CheckInTooSoonNoticeProps {
  /** Earliest bookable check-in date, `YYYY-MM-DD`. */
  firstBookableOn: string;
  className?: string;
}
const HEADING = 'TOO SOON TO BOOK';

/**
 * Empty state for a dated search the platform booking lead time emptied
 * (BHT-8058).
 *
 * A check-in inside the floor is excluded by the backend for every listing on
 * the platform, so the search comes back with nothing while the inventory is
 * untouched. The generic empty states read that as an inventory problem, which
 * is the defect this replaces.
 *
 * The copy runs why, then the date, then the reassurance, which is the order a
 * guest needs them in. The last sentence is the load-bearing one: it says
 * outright that the places are still there.
 *
 * It deliberately does not reproduce
 * `GraphWeb.BookingLeadTimeErrors.check_in_too_soon/1` word for word. That
 * sentence is an inline validation error on a quote, where the guest has
 * already picked a listing and is being told this attempt will not go through.
 * What the two surfaces owe each other is the same date, not the same
 * sentence, and `firstBookableCheckInDate` is what keeps that true.
 *
 * The reason carries no number on purpose. The floor is a constant that can be
 * retuned, and a hardcoded "about a week" would quietly go wrong the day it
 * changes while the date kept updating itself.
 *
 * It lives in `src/components/` rather than beside the search empty states
 * because both the search page and an account's listing page render it, and
 * those are different route groups.
 */
export const CheckInTooSoonNotice: React.FC<CheckInTooSoonNoticeProps> = t0 => {
  const $ = _c(10);
  const {
    firstBookableOn,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = twMerge("w-full text-gray-dark", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="text-xl font-semibold">{HEADING}</div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== firstBookableOn) {
    t3 = formatBookableCheckInDate(firstBookableOn);
    $[3] = firstBookableOn;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="text-lg font-medium pt-6">Bookings need more notice than that, so the earliest you can check in is{" "}{t3}. Pick a later date and you'll see everything we have.</div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t4) {
    t5 = <div data-id="@search/check-in-too-soon-notice" className={t1}>{t2}{t4}</div>;
    $[7] = t1;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};